import gql from 'graphql-tag';

import { Query } from 'core/index';

import { PROFILE_CONNECTION_FIELDS } from 'apps/profile/gql';
import { PROJECT_CONNECTION_FIELDS } from 'apps/project/gql';
import { CHANNEL_CONNECTION_FIELDS } from 'apps/channel/gql';
import { INITIATIVE_CONNECTION_FIELDS } from 'apps/initiative/gql/fragments';
import { STORY_CONNECTION_FIELDS } from 'apps/story/gql';
import { COLLECTION_CONNECTION_FIELDS } from 'apps/collection/gql';

const THREAD_CONNECTION_FIELDS = gql`
  fragment searchThreadConnectionFields on ThreadConnection {
    pageInfo {
      startCursor
      endCursor
      hasNextPage
    }
    totalCount
    edges {
      cursor
      node {
        id
        title
        forum {
          id
          parentContent {
            id
            slug
          }
        }
        category {
          id
          title
          color
          urlId
        }
        commentsCount
        dateCreated
        lastActivityAt
        creatorProfile {
          id
          username
          fullName
          imageFallbackChar
          avatar {
            id
            path
            url
          }
        }
        urlId
        content {
          id
          isLiked
          likesCount
          creator {
            id
            username
          }
        }
      }
    }
  }
`;

export const SEARCH = gql`
  ${PROFILE_CONNECTION_FIELDS}
  ${PROJECT_CONNECTION_FIELDS}
  ${CHANNEL_CONNECTION_FIELDS}
  ${THREAD_CONNECTION_FIELDS}
  ${INITIATIVE_CONNECTION_FIELDS}
  ${STORY_CONNECTION_FIELDS}
  ${COLLECTION_CONNECTION_FIELDS}
  query Search(
    $q: String!
    $profilesCount: Int
    $profilesCursor: String
    $profilesSortBy: String
    $projectsCount: Int
    $projectsCursor: String
    $projectsSortBy: String
    $channelsCount: Int
    $channelsCursor: String
    $channelsSortBy: String
    $threadsBatchSize: Int
    $threadsCursor: String
    $threadsSortBy: String
    $initiativesCount: Int
    $initiativesCursor: String
    $initiativesSortBy: String
    $storiesCount: Int
    $storiesCursor: String
    $storiesSortBy: String
    $collectionsCount: Int
    $collectionsCursor: String
    $collectionsSortBy: String
  ) {
    search(q: $q) {
      profiles(
        first: $profilesCount
        after: $profilesCursor
        sortBy: $profilesSortBy
      ) {
        ...profileConnectionFields
      }
      projects(
        first: $projectsCount
        after: $projectsCursor
        sortBy: $projectsSortBy
      ) {
        ...projectConnectionFields
      }
      channels(
        first: $channelsCount
        after: $channelsCursor
        sortBy: $channelsSortBy
      ) {
        ...channelConnectionFields
      }
      threads(
        first: $threadsBatchSize
        after: $threadsCursor
        sortBy: $threadsSortBy
      ) {
        ...searchThreadConnectionFields
      }
      initiatives(
        first: $initiativesCount
        after: $initiativesCursor
        sortBy: $initiativesSortBy
      ) {
        ...initiativeConnectionFields
      }
      stories(
        first: $storiesCount
        after: $storiesCursor
        sortBy: $storiesSortBy
      ) {
        ...storyConnectionFields
      }
      collections(
        first: $collectionsCount
        after: $collectionsCursor
        sortBy: $collectionsSortBy
      ) {
        ...collectionConnectionFields
      }
    }
  }
`;
export class SearchQuery extends Query {
  static conf = {
    GQL: SEARCH,
    varNames: [
      'q',
      // channels
      'channelsCursor',
      'channelsSortBy',
      'channelsCount',
      // profiles
      'profilesCursor',
      'profilesCount',
      'profilesSortBy',
      // projects
      'projectsCursor',
      'projectsCount',
      'projectsSortBy',
      // threads
      'threadsCursor',
      'threadsBatchSize',
      'threadsSortBy',
      // initiative
      'initiativesCursor',
      'initiativesCount',
      'initiativesSortBy',
      // stories
      'storiesCursor',
      'storiesCount',
      'storiesSortBy',
      // collections
      'collectionsCursor',
      'collectionsCount',
      'collectionsSortBy',
    ],
    resultPath: 'search',
  };
}

const PROFILES_SEARCH = gql`
  ${PROFILE_CONNECTION_FIELDS}
  query ProfilesSearch(
    $q: String!
    $profilesCount: Int
    $profilesCursor: String
    $profilesSortBy: String
  ) {
    search(q: $q) {
      profiles(
        first: $profilesCount
        after: $profilesCursor
        sortBy: $profilesSortBy
      ) {
        ...profileConnectionFields
      }
    }
  }
`;
export class ProfilesSearchQuery extends Query {
  static conf = {
    GQL: PROFILES_SEARCH,
    varNames: ['q', 'profilesCursor', 'profilesCount', 'profilesSortBy'],
    resultPath: 'search.profiles',
  };
}

const PROJECTS_SEARCH = gql`
  ${PROJECT_CONNECTION_FIELDS}
  query ProjectsSearch(
    $q: String!
    $projectsCount: Int
    $projectsCursor: String
    $projectsSortBy: String
  ) {
    search(q: $q) {
      projects(
        first: $projectsCount
        after: $projectsCursor
        sortBy: $projectsSortBy
      ) {
        ...projectConnectionFields
      }
    }
  }
`;
export class ProjectsSearchQuery extends Query {
  static conf = {
    GQL: PROJECTS_SEARCH,
    varNames: ['q', 'projectsCursor', 'projectsCount', 'projectsSortBy'],
    resultPath: 'search.projects',
  };
}

const CHANNELS_SEARCH = gql`
  ${CHANNEL_CONNECTION_FIELDS}
  query ProjectsSearch(
    $q: String!
    $channelsCount: Int
    $channelsCursor: String
    $channelsSortBy: String
  ) {
    search(q: $q) {
      channels(
        first: $channelsCount
        after: $channelsCursor
        sortBy: $channelsSortBy
      ) {
        ...channelConnectionFields
      }
    }
  }
`;
export class ChannelsSearchQuery extends Query {
  static conf = {
    GQL: CHANNELS_SEARCH,
    varNames: ['q', 'channelsCursor', 'channelsSortBy', 'channelsCount'],
    resultPath: 'search.channels',
  };
}

const THREADS_SEARCH = gql`
  ${THREAD_CONNECTION_FIELDS}
  query ThreadsSearch(
    $q: String!
    $threadsBatchSize: Int
    $threadsCursor: String
    $threadsSortBy: String
  ) {
    search(q: $q) {
      threads(
        first: $threadsBatchSize
        after: $threadsCursor
        sortBy: $threadsSortBy
      ) {
        ...searchThreadConnectionFields
      }
    }
  }
`;
export class ThreadsSearchQuery extends Query {
  static conf = {
    GQL: THREADS_SEARCH,
    varNames: ['q', 'threadsCursor', 'threadsBatchSize', 'threadsSortBy'],
    resultPath: 'search.threads',
  };
}

const INITIATIVES_SEARCH = gql`
  ${INITIATIVE_CONNECTION_FIELDS}
  query InitiativesSearch(
    $q: String!
    $initiativesCount: Int
    $initiativesCursor: String
    $initiativesSortBy: String
  ) {
    search(q: $q) {
      initiatives(
        first: $initiativesCount
        after: $initiativesCursor
        sortBy: $initiativesSortBy
      ) {
        ...initiativeConnectionFields
      }
    }
  }
`;
export class InitiativesSearchQuery extends Query {
  static conf = {
    GQL: INITIATIVES_SEARCH,
    varNames: [
      'q',
      'initiativesCursor',
      'initiativesCount',
      'initiativesSortBy',
    ],
    resultPath: 'search.initiatives',
  };
}

const STORIES_SEARCH = gql`
  ${STORY_CONNECTION_FIELDS}
  query StoriesSearch(
    $q: String!
    $storiesCount: Int
    $storiesCursor: String
    $storiesSortBy: String
  ) {
    search(q: $q) {
      stories(
        first: $storiesCount
        after: $storiesCursor
        sortBy: $storiesSortBy
      ) {
        ...storyConnectionFields
      }
    }
  }
`;
export class StoriesSearchQuery extends Query {
  static conf = {
    GQL: STORIES_SEARCH,
    varNames: ['q', 'storiesCursor', 'storiesCount', 'storiesSortBy'],
    resultPath: 'search.stories',
  };
}

const COLLECTIONS_SEARCH = gql`
  ${COLLECTION_CONNECTION_FIELDS}
  query StoriesSearch(
    $q: String!
    $collectionsCount: Int
    $collectionsCursor: String
    $collectionsSortBy: String
  ) {
    search(q: $q) {
      collections(
        first: $collectionsCount
        after: $collectionsCursor
        sortBy: $collectionsSortBy
      ) {
        ...collectionConnectionFields
      }
    }
  }
`;
export class CollectionsSearchQuery extends Query {
  static conf = {
    GQL: COLLECTIONS_SEARCH,
    varNames: [
      'q',
      'collectionsCursor',
      'collectionsCount',
      'collectionsSortBy',
    ],
    resultPath: 'search.collections',
  };
}
